/**
 * Base Supabase Service Class
 * Provides common database operations using the Supabase JS client.
 * Mirrors the interface of the old FirebaseService so existing services
 * can extend this class with minimal changes.
 */

import { supabase } from '../../utils/supabase_config';

export class SupabaseService {
  /**
   * Map a row (or array of rows) to include docID for backward compatibility.
   * All components reference record.docID — this preserves that without
   * touching any component code.
   */
  static _addDocId(row) {
    return { ...row, docID: row.id };
  }

  static _formatRows(rows) {
    return rows.map(row => this._addDocId(row));
  }

  /**
   * Get all rows from a table with optional filters.
   * @param {string} tableName
   * @param {Object} options - { where: [{field, operator, value}], orderBy: {field, direction}, limit }
   */
  static async getAll(tableName, options = {}) {
    try {
      let query = supabase.from(tableName).select('*');

      if (options.where) {
        for (const condition of options.where) {
          const { field, operator, value } = condition;
          if (operator === '==') {
            query = query.eq(field, value);
          } else if (operator === '!=') {
            query = query.neq(field, value);
          } else if (operator === '>') {
            query = query.gt(field, value);
          } else if (operator === '>=') {
            query = query.gte(field, value);
          } else if (operator === '<') {
            query = query.lt(field, value);
          } else if (operator === '<=') {
            query = query.lte(field, value);
          }
        }
      }

      if (options.orderBy) {
        query = query.order(options.orderBy.field, {
          ascending: (options.orderBy.direction || 'asc') === 'asc'
        });
      }

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;
      if (error) throw error;
      return this._formatRows(data || []);
    } catch (error) {
      this.handleError(`Error getting rows from ${tableName}`, error);
      throw error;
    }
  }

  /**
   * Get a single row by primary key (id).
   * @param {string} tableName
   * @param {string} id - UUID primary key
   */
  static async getById(tableName, id) {
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data ? this._addDocId(data) : null;
    } catch (error) {
      this.handleError(`Error getting row ${id} from ${tableName}`, error);
      throw error;
    }
  }

  /**
   * Get rows matching one or more WHERE conditions.
   * @param {string} tableName
   * @param {Array} whereConditions - [{field, operator, value}]
   */
  static async getWhere(tableName, whereConditions) {
    return this.getAll(tableName, { where: whereConditions });
  }

  /**
   * Insert a new row.
   * @param {string} tableName
   * @param {Object} data
   * @returns {Promise<string>} ID of the created row
   */
  static async create(tableName, data) {
    try {
      const { data: created, error } = await supabase
        .from(tableName)
        .insert(data)
        .select('id')
        .single();
      if (error) throw error;
      return created.id;
    } catch (error) {
      this.handleError(`Error creating row in ${tableName}`, error);
      throw error;
    }
  }

  /**
   * Update an existing row by id.
   * @param {string} tableName
   * @param {string} id
   * @param {Object} updates
   */
  static async update(tableName, id, updates) {
    try {
      const { error } = await supabase
        .from(tableName)
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    } catch (error) {
      this.handleError(`Error updating row ${id} in ${tableName}`, error);
      throw error;
    }
  }

  /**
   * Delete a row by id.
   * @param {string} tableName
   * @param {string} id
   */
  static async delete(tableName, id) {
    try {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', id);
      if (error) throw error;
    } catch (error) {
      this.handleError(`Error deleting row ${id} from ${tableName}`, error);
      throw error;
    }
  }

  /**
   * Call a Supabase/PostgreSQL RPC function.
   * @param {string} functionName
   * @param {Object} params
   */
  static async rpc(functionName, params = {}) {
    try {
      const { data, error } = await supabase.rpc(functionName, params);
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(`Error calling RPC ${functionName}`, error);
      throw error;
    }
  }

  static handleError(context, error) {
    console.error(`${context}:`, error);
  }

  static getUserFriendlyMessage(error) {
    if (error?.code === 'PGRST116') return 'The requested data was not found.';
    if (error?.code === '42501') return 'You don\'t have permission to perform this action.';
    return 'An unexpected error occurred. Please try again.';
  }
}
